import { type ReactNode, useState } from 'react';
import {
  Box,
  Button as MantineButton,
  Center,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconX } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { Button } from 'src/design-system/Button';
import { IconButton } from 'src/design-system/IconButton';
import { useClient } from 'src/clients/hooks/useClient';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFileUpload } from 'src/data-capture/hooks/file-uploads/useFileUpload';
import { useFamilyFileDetections } from 'src/data-capture/hooks/file-uploads/useFamilyFileDetections';
import { MeetingPlatformIcon } from 'src/meetings/components/MeetingPlatformIcon';
import { useBotStatus } from 'src/meetings/hooks/useBotStatus';
import { useForceBotJoin } from 'src/meetings/hooks/useForceBotJoin';
import { type FamilyMeeting, useScopedMeetings } from 'src/meetings/hooks/useScopedMeetings';
import { assignSpeakerPath, meetingsBasePath, type MeetingScope } from 'src/meetings/scope';
import { formatSpeakerLabel, getUnidentifiedSpeakers } from 'src/meetings/utils/speaker-utils';
import {
  MeetingNoteStatus,
  useCalendarEventMeetingNote,
} from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import { TranscriptionStatus } from 'src/meetings/detail/tabs/transcript/types';
import { formatMeetingDuration, isPastEvent } from 'src/meetings/utils/date-utils';
import {
  getAllClientParticipants,
  getParticipantDisplayNames,
} from 'src/meetings/utils/event-utils';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import { AddExpectedAttendeesModal } from './AddExpectedAttendeesModal';
import FamilyConfirmModal from './FamilyConfirmModal';
import { GenerateMeetingNoteCard } from './GenerateMeetingNoteCard';
import { GenerateMeetingNoteModal } from './GenerateMeetingNoteModal';
import { MeetingAgendaCard } from './MeetingAgendaCard';
import { MeetingDetailsCard } from './MeetingDetailsCard';
import { ParticipantInfoCard } from './ParticipantInfoCard';
import { ParticipantPill } from './ParticipantPill';
import { PastMeetingTabs } from './PastMeetingTabs';
import { SuggestedActionCard } from './SuggestedActionCard';
import { SuggestedActions } from './SuggestedActions';

export default function MeetingDetailsScreen() {
  const {
    familyId,
    clientId,
    eventId = '',
  } = useParams<{ familyId?: string; clientId?: string; eventId: string }>();
  const navigate = useNavigate();
  const scope: MeetingScope = familyId
    ? { kind: 'family', familyId }
    : { kind: 'individual', clientId: clientId ?? '' };
  const { events, isLoading } = useScopedMeetings(scope);
  const { family } = useFamily(familyId);
  const { clients } = useClients({ familyId }, { enabled: !!familyId });
  const { client: individualClient } = useClient(clientId);
  const { forceBotJoin, isLoading: isBotJoinLoading } = useForceBotJoin(eventId);
  const { botStatus, isLoading: isBotStatusLoading } = useBotStatus(eventId);
  const [attendeesOpen, setAttendeesOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [unreviewedProfileUpdate, setUnreviewedProfileUpdate] = useState<{
    id: string;
    firstName: string;
  } | null>(null);

  const meeting = events.find(event => event.id === eventId) ?? null;
  const meetingsPath = meetingsBasePath(scope);
  const isFamilyScope = scope.kind === 'family';

  // A finished meeting with a ready transcript can have a meeting note generated.
  const isPast = meeting ? isPastEvent(meeting) : false;
  const { transcript } = useTranscript({
    eventId,
    enabled: isPast && !!meeting?.hasTranscription,
  });
  const transcriptReady = transcript?.status === TranscriptionStatus.READY;
  const { meetingNote } = useCalendarEventMeetingNote(eventId, {
    enabled: isPast && transcriptReady,
  });
  const noteReady = meetingNote?.status === MeetingNoteStatus.READY;
  const noteExistsOrGenerating =
    noteReady || meetingNote?.status === MeetingNoteStatus.MEETING_NOTE_PROCESSING;
  // Only offer to generate a note when the transcript is ready and there isn't one already.
  const canCreateNote = isPast && transcriptReady && !noteExistsOrGenerating;

  // A meeting note's detections are reviewed per family member; we hide a member's
  // "update profile" suggestion once their detection row is processed.
  const dataUploadFileId = transcript?.dataUploadFileId;
  const { members: familyDetectionMembers } = useFamilyFileDetections(
    scope.kind === 'family' ? scope.familyId : undefined,
    dataUploadFileId,
    { enabled: isPast && noteReady },
  );
  const { file: recordingFile } = useFileUpload(dataUploadFileId, {
    clientId: scope.kind === 'individual' ? scope.clientId : undefined,
    enabled: isPast && noteReady && scope.kind === 'individual',
  });

  if (isLoading && !meeting) {
    return (
      <Center py="xxl">
        <Loader />
      </Center>
    );
  }

  if (!meeting) {
    return <Text c="gray.6">This meeting could not be found.</Text>;
  }

  const meetingUrl = meeting.meetingUrl;
  const enrichedClients = individualClient ? [...clients, individualClient] : clients;
  const clientById = Object.fromEntries(enrichedClients.map(client => [client.id, client]));

  const clientParticipants = getAllClientParticipants(meeting)
    .map(p => p.participant.client)
    .filter((client): client is NonNullable<typeof client> => client !== null);

  const participantNames = getParticipantDisplayNames(meeting);

  const participantInfo = clientParticipants.map(simple => {
    const full = clientById[simple.id];
    return {
      id: simple.id,
      firstName: simple.firstName,
      name: `${simple.firstName} ${simple.lastName}`.trim(),
      address: full?.personalDetails?.address ?? null,
      email: full?.emailAddress ?? simple.emailAddress ?? null,
      phone: full?.personalDetails?.phoneNumber ?? null,
    };
  });

  const resolvedClientIds = new Set<string>();
  if (scope.kind === 'family') {
    for (const member of familyDetectionMembers) {
      if (member.isProcessed) resolvedClientIds.add(member.client.id);
    }
  } else if (recordingFile?.isResolved) {
    resolvedClientIds.add(scope.clientId);
  }
  const unresolvedParticipants = participantInfo.filter(p => !resolvedClientIds.has(p.id));

  const unidentifiedSpeakers = transcriptReady ? getUnidentifiedSpeakers(transcript?.segments) : [];

  const mainClient = family?.mainFamilyMember.clientId
    ? clientById[family.mainFamilyMember.clientId]
    : undefined;
  const familyName = family?.name ?? 'Family';
  const mainMemberName = mainClient
    ? formatName(mainClient.firstName, mainClient.lastName, NameFormat.NATURAL)
    : (participantInfo[0]?.name ?? 'this client');
  const overviewName = isFamilyScope ? familyName : mainMemberName;

  const openMeeting = () => {
    if (meetingUrl) window.open(meetingUrl, '_blank');
  };

  const handleForceBotJoin = async () => {
    try {
      await forceBotJoin();
      notifications.show({
        title: 'Bot joining meeting',
        message: 'The meeting bot will join within 10 seconds',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Failed to schedule bot',
        message: 'Could not reschedule the bot to join now. Please try again.',
        color: 'red',
      });
    }
  };

  const handleJoinMeetingClick = () => {
    const minutesFromStart =
      (Date.now() - new Date(meeting.scheduledStartAt).getTime()) / 1000 / 60;

    Mixpanel.track(MixpanelEvents.BOT_JOIN_MEETING_CLICKED, {
      botStatus: botStatus || 'UNKNOWN',
      minutesFromStart: Math.abs(minutesFromStart),
      meetingUrl,
    });

    if (
      botStatus === 'ACTIVE' ||
      botStatus === 'NONE' ||
      botStatus === 'FINISHED' ||
      Math.abs(minutesFromStart) <= 2
    ) {
      openMeeting();
      return;
    }

    setRescheduleOpen(true);
  };

  const handleConfirmJoin = async (rescheduleBot: boolean) => {
    Mixpanel.track(MixpanelEvents.BOT_JOIN_MEETING_CONFIRMED, { rescheduleBot, meetingUrl });
    if (rescheduleBot) await handleForceBotJoin();
    setRescheduleOpen(false);
    openMeeting();
  };

  const handleAddBotClick = async () => {
    Mixpanel.track(MixpanelEvents.BOT_MANUALLY_ADDED_TO_MEETING);
    await handleForceBotJoin();
  };

  const handleReviewDetections = (participantClientId: string) => {
    const params = new URLSearchParams({
      fileId: transcript?.dataUploadFileId ?? '',
      fileName: meeting.title,
      guid: uuidv4(),
      originMeetingId: meeting.id,
    });
    navigate(`/client/${participantClientId}/detected-entities?${params.toString()}`);
  };

  const handleUpdateProfile = (participant: { id: string; firstName: string }) => {
    if (meetingNote?.isReviewed) {
      handleReviewDetections(participant.id);
    } else {
      setUnreviewedProfileUpdate(participant);
    }
  };

  const dateTimePill = (
    <Flex align="center" p="xxs" bg="gray.1" style={{ borderRadius: 12, width: 'fit-content' }}>
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.7">
        {formatSchedule(meeting)}
      </Text>
    </Flex>
  );

  // Only one kind of suggested action is offered at a time. Identifying unknown
  // speakers comes first so the meeting note is built with correct labels, then
  // generating the note, then reviewing member profiles.
  let suggestedActions: ReactNode = null;
  if (unidentifiedSpeakers.length > 0) {
    suggestedActions = (
      <SuggestedActions>
        {unidentifiedSpeakers.map(speaker => (
          <SuggestedActionCard
            key={speaker.label}
            name={`Identify ${formatSpeakerLabel(speaker.label)}`}
            description={`${speaker.statementCount} statement${
              speaker.statementCount === 1 ? '' : 's'
            } from an unidentified speaker`}
            onClick={() => navigate(assignSpeakerPath(scope, meeting.id, speaker.label))}
          />
        ))}
      </SuggestedActions>
    );
  } else if (canCreateNote) {
    suggestedActions = (
      <SuggestedActions>
        <GenerateMeetingNoteCard
          participantNames={participantNames}
          onEdit={() => setNoteModalOpen(true)}
          onGenerate={() => setNoteModalOpen(true)}
        />
      </SuggestedActions>
    );
  } else if (noteReady && unresolvedParticipants.length > 0) {
    suggestedActions = (
      <SuggestedActions>
        {unresolvedParticipants.map(participant => (
          <SuggestedActionCard
            key={participant.id}
            name={participant.name}
            description="Update profile from meeting"
            onClick={() =>
              handleUpdateProfile({
                id: participant.id,
                firstName: participant.firstName,
              })
            }
          />
        ))}
      </SuggestedActions>
    );
  }

  return (
    <>
      <Box pt={36} px="xxl" pb="xxl">
        {isPast ? (
          <Flex align="center" gap="xxs">
            <IconButton
              size={36}
              bg="gray.1"
              c="gray.9"
              aria-label="Back to meetings"
              onClick={() => navigate(meetingsPath)}
              style={{ borderRadius: 12 }}
            >
              <IconArrowLeft size={20} />
            </IconButton>
            {dateTimePill}
            {participantNames.length > 0 && (
              <ParticipantPill
                participantNames={participantNames}
                onOpen={() => setAttendeesOpen(true)}
                radius={12}
                textSize="textSm"
                nameColor="gray.10"
                iconSize={20}
                px="xs"
                py="xxs"
                gap={8}
              />
            )}
          </Flex>
        ) : (
          <Flex justify="space-between" align="flex-start" gap="sm">
            {dateTimePill}
            <IconButton
              size={40}
              bg="gray.1"
              c="gray.9"
              aria-label="Close"
              onClick={() => navigate(meetingsPath)}
            >
              <IconX size={20} />
            </IconButton>
          </Flex>
        )}

        <Flex align="center" mt="xs" py={5}>
          {meeting.meetingPlatform && (
            <Box style={{ paddingRight: 12 }}>
              <MeetingPlatformIcon platform={meeting.meetingPlatform} size={28} />
            </Box>
          )}
          <Text size="displaySm" fw={FONT_WEIGHTS.medium} c="gray.10">
            {meeting.title}
          </Text>
        </Flex>

        {isPast ? (
          <>
            {suggestedActions && <Box mt="sm">{suggestedActions}</Box>}
            <Box mt="lg">
              <PastMeetingTabs meeting={meeting} transcript={transcript} />
            </Box>
          </>
        ) : (
          <>
            <Flex justify="space-between" align="center" mt="sm" gap="sm">
              {participantNames.length > 0 ? (
                <ParticipantPill
                  participantNames={participantNames}
                  onOpen={() => setAttendeesOpen(true)}
                  radius={12}
                  textSize="textSm"
                  nameColor="gray.10"
                  iconSize={20}
                />
              ) : (
                <Box />
              )}
              <Group gap="sm" wrap="nowrap">
                <Button
                  variant="primary"
                  size="sm"
                  w={160}
                  h={36}
                  style={{ borderRadius: 12 }}
                  disabled={!meetingUrl || isBotJoinLoading}
                  onClick={handleJoinMeetingClick}
                >
                  Join meeting
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  w={160}
                  h={36}
                  style={{ borderRadius: 12 }}
                  loading={isBotJoinLoading || isBotStatusLoading}
                  disabled={botStatus === 'ACTIVE' || !meetingUrl}
                  onClick={handleAddBotClick}
                >
                  {botStatus === 'ACTIVE' ? 'Bot recording' : 'Add bot'}
                </Button>
              </Group>
            </Flex>

            <Divider mt="md" color="gray.1" />

            <Text mt="lg" size="xl" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Meeting preparation
            </Text>

            <Stack mt="sm" gap="sm">
              <MeetingDetailsCard title={`${overviewName} overview`} />

              {participantInfo.map(participant => (
                <ParticipantInfoCard
                  key={participant.id}
                  name={participant.name}
                  address={participant.address}
                  email={participant.email}
                  phone={participant.phone}
                />
              ))}

              <MeetingAgendaCard eventId={meeting.id} hasMeetingAgenda={meeting.hasMeetingAgenda} />
            </Stack>
          </>
        )}
      </Box>

      <AddExpectedAttendeesModal
        opened={attendeesOpen}
        onClose={() => setAttendeesOpen(false)}
        scope={scope}
        meeting={meeting}
      />

      <GenerateMeetingNoteModal
        opened={noteModalOpen}
        onClose={() => setNoteModalOpen(false)}
        scope={scope}
        meeting={meeting}
      />

      <FamilyConfirmModal
        opened={!!unreviewedProfileUpdate}
        onClose={() => setUnreviewedProfileUpdate(null)}
        onConfirm={() => {
          if (unreviewedProfileUpdate) handleReviewDetections(unreviewedProfileUpdate.id);
          setUnreviewedProfileUpdate(null);
        }}
        title="Meeting note isn't reviewed."
        description={`Are you sure you want to proceed and update ${unreviewedProfileUpdate?.firstName ?? ''}'s profile with unreviewed data?`}
        confirmLabel="Continue anyway"
        cancelLabel="Back to review"
        isLoading={false}
      />

      <Modal
        opened={rescheduleOpen}
        onClose={() => setRescheduleOpen(false)}
        title="Reschedule meeting bot?"
        centered
      >
        <Text size="textMd" mb="xs">
          Do you want to reschedule the bot to join the meeting immediately?
        </Text>
        <Text size="textSm" c="gray.6" mb="lg">
          If the meeting is starting early or you want the bot to join now, select &quot;Yes&quot;.
          Otherwise, the bot will join at the scheduled time.
        </Text>
        <Group justify="flex-end" gap="sm">
          <MantineButton
            variant="subtle"
            onClick={() => handleConfirmJoin(false)}
            disabled={isBotJoinLoading}
          >
            No, just join
          </MantineButton>
          <MantineButton
            variant="filled"
            onClick={() => handleConfirmJoin(true)}
            loading={isBotJoinLoading}
          >
            Yes, reschedule bot
          </MantineButton>
        </Group>
      </Modal>
    </>
  );
}

function formatSchedule(meeting: FamilyMeeting): string {
  const startIso = meeting.startAt ?? meeting.scheduledStartAt;
  const endIso = meeting.endAt ?? meeting.scheduledEndAt;
  const start = new Date(startIso);
  const end = new Date(endIso);

  const date = start.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });

  const startMeridiem = start.getHours() >= 12 ? 'PM' : 'AM';
  const endMeridiem = end.getHours() >= 12 ? 'PM' : 'AM';
  const startText = formatClock(start, startMeridiem !== endMeridiem);
  const endText = formatClock(end, true);

  return `${date}, ${startText}-${endText} (${formatMeetingDuration(startIso, endIso)})`;
}

function formatClock(date: Date, withMeridiem: boolean): string {
  const hours = date.getHours() % 12 || 12;
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const meridiem = date.getHours() >= 12 ? 'PM' : 'AM';
  return withMeridiem ? `${hours}:${minutes}${meridiem}` : `${hours}:${minutes}`;
}
